/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react'
import { Modal, Row, Col, Form } from 'react-bootstrap'
import { addLab, updateLab } from '@store/actions/lab.action'
import Button from '@components/common/Button/Button.component'
import LabSharedTable from '@components/pages/Lab/LabSharedTable'
import ActionButtons from '@components/common/ActionButtons/ActionButtons.component'
import { v4 as uuidv4 } from 'uuid'
import { Laboratory } from '@dataTypes/lab.types'
import ReactInputMask from 'react-input-mask'

const InputMask = ReactInputMask as unknown as React.FC<any>

import { useAppDispatch } from '@store/hooks'

interface AddLaboratoryProps {
  show: boolean
  onClose: () => void
  editLab?: Laboratory | null
  isEditMode?: boolean
}

interface Service {
  type: string
  turnaroundDays: string
  expedite: string
}

const usPhoneRegex = /^(\([0-9]{3}\)|[0-9]{3})[- ]?[0-9]{3}[- ]?[0-9]{4}$/

const AddLaboratory: React.FC<AddLaboratoryProps> = ({
  show,
  onClose,
  editLab,
  isEditMode,
}) => {
  const dispatch = useAppDispatch()

  const [name, setName] = useState<string>('')
  const [phone, setPhone] = useState<string>('')
  const [address, setAddress] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [isActive, setIsActive] = useState<boolean>(true)
  const [services, setServices] = useState<Service[]>([])
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const [editedService, setEditedService] = useState<Service | null>(null)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [phoneError, setPhoneError] = useState<string | null>(null)

  useEffect(() => {
    if (isEditMode && editLab) {
      setName(editLab.name)
      setPhone(editLab.phoneNumber)
      setAddress(editLab.address)
      setEmail(editLab.email)
      setNotes(editLab.notes)
      setIsActive(editLab.isActive)
      setServices(editLab.services || [])
    } else {
      setName('')
      setPhone('')
      setAddress('')
      setEmail('')
      setNotes('')
      setIsActive(true)
      setServices([])
    }
    setEditingIndex(null)
    setEditedService(null)
  }, [show, isEditMode, editLab])

  const handleAddService = (): void => {
    const newService = { type: '', turnaroundDays: '', expedite: 'No' }
    setServices([...services, newService])
    setEditedService(newService)
    setEditingIndex(services.length)
  }

  const handleServiceChange = (field: keyof Service, value: string): void => {
    if (!editedService) return
    setEditedService({ ...editedService, [field]: value })
  }

  const handleSaveService = (index: number): void => {
    if (
      !editedService?.type ||
      !editedService.turnaroundDays ||
      !editedService.expedite
    ) {
      alert('Please complete all fields in the service.')
      return
    }
    const updated = [...services]
    updated[index] = editedService
    setServices(updated)
    setEditingIndex(null)
    setEditedService(null)
  }

  const handleCancelEdit = (): void => {
    setEditingIndex(null)
    setEditedService(null)
  }

  const handleRemoveService = (index: number): void => {
    const updated = [...services]
    updated.splice(index, 1)
    setServices(updated)
    setEditingIndex(null)
  }

  useEffect(() => {
    setPhoneError(
      phone && !usPhoneRegex.test(phone)
        ? 'Invalid US phone number format'
        : null,
    )
  }, [phone])

  const handleSave = (): void => {
    if (editingIndex !== null) {
      alert(
        'Please save or cancel the service you are editing before submitting.',
      )
      return
    }

    const requiredFields = [
      { label: 'Lab name', value: name.trim() },
      { label: 'Phone number', value: phone.trim() },
      { label: 'Address', value: address.trim() },
      { label: 'Email', value: email.trim() },
    ]

    const emptyField = requiredFields.find((f) => !f.value)
    if (emptyField) {
      alert(`Please enter ${emptyField.label}.`)
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      alert('Please enter a valid email address.')
      return
    }

    if (services.length === 0) {
      alert('Please add at least one service.')
      return
    }

    const hasEmptyService = services.some(
      (s) => !s.type || !s.turnaroundDays || !s.expedite,
    )
    if (hasEmptyService) {
      alert('All service fields must be completed.')
      return
    }

    const payload: Laboratory = {
      id: isEditMode && editLab ? editLab.id : uuidv4(),
      name,
      phoneNumber: phone,
      address,
      email,
      notes,
      isActive,
      services,
    }

    dispatch(isEditMode ? updateLab(payload) : addLab(payload))
    onClose()
  }

  const serviceColumns = [
    {
      key: 'type',
      label: 'Service type',
      formatter: (value: any, row: Service) => {
        const index = services.findIndex((s) => s === row)
        return editingIndex === index ? (
          <Form.Select
            value={editedService?.type || ''}
            onChange={(e) => handleServiceChange('type', e.target.value)}
          >
            <option value="">Select service</option>
            <option value="Crowns">Crowns</option>
            <option value="Dentures">Dentures</option>
            <option value="Veneers">Veneers</option>
            <option value="Bridges">Bridges</option>
            <option value="Mouthguards">Mouthguards</option>
          </Form.Select>
        ) : (
          row.type
        )
      },
    },
    {
      key: 'turnaroundDays',
      label: 'Turnaround days',
      formatter: (value: any, row: Service) => {
        const index = services.findIndex((s) => s === row)
        return editingIndex === index ? (
          <Form.Select
            value={editedService?.turnaroundDays || ''}
            onChange={(e) =>
              handleServiceChange('turnaroundDays', e.target.value)
            }
          >
            <option value="">Select days</option>
            {[...Array(7)].map((_, i) => {
              const d = `${i + 1} day${i > 0 ? 's' : ''}`
              return (
                <option key={d} value={d}>
                  {d}
                </option>
              )
            })}
          </Form.Select>
        ) : (
          row.turnaroundDays
        )
      },
    },
    {
      key: 'expedite',
      label: 'Expedite service',
      formatter: (value: any, row: Service) => {
        const index = services.findIndex((s) => s === row)
        return editingIndex === index ? (
          <Form.Select
            value={editedService?.expedite || ''}
            onChange={(e) => handleServiceChange('expedite', e.target.value)}
          >
            <option value="No">No</option>
            {[...Array(7)].map((_, i) => {
              const d = `Yes (${i + 1} Day${i > 0 ? 's' : ''})`
              return (
                <option key={d} value={d}>
                  {d}
                </option>
              )
            })}
          </Form.Select>
        ) : (
          row.expedite
        )
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      formatter: (_value: any, row: Service) => {
        const index = services.findIndex((s) => s === row)
        return (
          <div
            className="d-flex justify-content-center align-items-center gap-0"
            style={{ height: '32px' }}
          >
            {editingIndex === index ? (
              <>
                <button
                  title="Save"
                  className="action-button"
                  onClick={() => handleSaveService(index)}
                >
                  <i className="ti ti-check text-success fs-5" />
                </button>
                <button
                  title="Cancel"
                  className="action-button"
                  onClick={handleCancelEdit}
                >
                  <i className="ti ti-x text-danger fs-5" />
                </button>
              </>
            ) : (
              <ActionButtons
                showView={false}
                showEdit
                showDelete
                onEdit={() => {
                  setEditingIndex(index)
                  setEditedService({ ...row })
                }}
                onDelete={() => handleRemoveService(index)}
              />
            )}
          </div>
        )
      },
    },
  ]

  const isFormValid = (): boolean => {
    return (
      !!name.trim() &&
      !!phone.trim() &&
      usPhoneRegex.test(phone) &&
      !!address.trim() &&
      !!email.trim() &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
      services.length > 0 &&
      services.every((s) => s.type && s.turnaroundDays && s.expedite) &&
      editingIndex === null
    )
  }

  return (
    <Modal show={show} onHide={onClose} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{isEditMode ? 'Edit' : 'Add'} laboratory</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Row className="mb-2 gx-2">
            <Col className="add-lab-input">
              <Form.Group>
                <Form.Label>Lab name</Form.Label>
                <Form.Control
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Form.Group>
            </Col>
            <Col className="add-lab-input">
              <Form.Group>
                <Form.Label>Phone number</Form.Label>
                <InputMask
                  mask="(999) 999-9999"
                  value={phone}
                  onChange={(e: any) => setPhone(e.target.value)}
                >
                  {(inputProps: any) => (
                    <Form.Control
                      {...inputProps}
                      type="tel"
                      isInvalid={!!phoneError}
                      placeholder="(123) 456-7890"
                    />
                  )}
                </InputMask>
                <Form.Control.Feedback type="invalid">
                  Please enter a valid US phone number.
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col className="add-lab-input">
              <Form.Group>
                <Form.Label>Address</Form.Label>
                <Form.Control
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-2 gx-2">
            <Col className="add-lab-input">
              <Form.Group>
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  isInvalid={!!emailError}
                  onChange={(e) => {
                    const val = e.target.value
                    setEmail(val)
                    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
                    setEmailError(
                      val && !regex.test(val) ? 'Invalid email format' : null,
                    )
                  }}
                />
                <Form.Control.Feedback type="invalid">
                  {emailError}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group>
                <Form.Label>Is active?</Form.Label>
                <Form.Select
                  value={isActive.toString()}
                  onChange={(e) => setIsActive(e.target.value === 'true')}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-2">
            <Col>
              <Form.Label className="mb-0">Notes</Form.Label>
              <div className="lab-note-box">
                <div className="lab-note-field">
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Write your notes here..."
                  />
                </div>
                <div className="lab-note-footer d-flex justify-content-start align-items-center">
                  <span className="lab-note-icon note-icon-bg">
                    <i className="mgc mgc_pencil_2_ai_fill" />
                  </span>
                  <span className="lab-note-icon note-icon-bg d-flex align-items-center">
                    <i className="mgc mgc_refresh_2_line" />{' '}
                    <span className="fs-small">Rewrite</span>
                  </span>
                  <span className="lab-note-icon note-icon-bg d-flex align-items-center">
                    <i className="mgc mgc_magic_3_fill" />
                    <span className="fs-small">Enhance</span>
                  </span>
                </div>
              </div>
            </Col>
          </Row>

          <Row className="mb-1 d-flex justify-content-between align-items-center">
            <Col>
              <h6 className="mb-0">Services offered</h6>
            </Col>
            <Col className="text-end">
              <Button
                type="button"
                className="button-primary mb-1"
                size="sm"
                onClick={handleAddService}
              >
                Add Service
              </Button>
            </Col>
          </Row>

          <div className="lab-services-offered-table">
            <LabSharedTable
              columns={serviceColumns}
              data={services}
              pageSize={10}
            />
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button className="button-secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          className="button-primary"
          onClick={handleSave}
          disabled={!isFormValid()}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default AddLaboratory
